import { parseEmailFromAddress } from "./email-from";
import type { AnnouncementCampaignStatus } from "./types";

export const DEFAULT_EXCLUDE_EMAILED_WITHIN_HOURS = 24;
export const MAX_EXCLUDE_EMAILED_WITHIN_HOURS = 168;
export const ANNOUNCEMENT_CAMPAIGN_POLL_MS = 2000;
export const ANNOUNCEMENT_CAMPAIGN_LOCK_MS = 2 * 60 * 1000;
export const ANNOUNCEMENT_PROCESS_BATCH_SIZE = 25;
export const ANNOUNCEMENT_DEFAULT_DELAY_MS = 500;
export const ANNOUNCEMENT_SCHEDULE_TIMER_MAX_MS = 24 * 60 * 60 * 1000;
export const ANNOUNCEMENT_RESEND_TAG = "announcement";
export const MAX_RESEND_LIST_PAGES = 20;

export function parseExcludeEmailedWithinHours(
  value: unknown,
): number | null {
  if (value === true) {
    return DEFAULT_EXCLUDE_EMAILED_WITHIN_HOURS;
  }
  if (value == null || value === "" || value === false) {
    return null;
  }

  const parsed =
    typeof value === "number" ? value : Number.parseInt(String(value), 10);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }

  return Math.min(Math.floor(parsed), MAX_EXCLUDE_EMAILED_WITHIN_HOURS);
}

export function excludeEmailedSince(
  hours: number,
  now: Date = new Date(),
): Date {
  return new Date(now.getTime() - hours * 60 * 60 * 1000);
}

export function normalizeAnnouncementEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function unionEmailSets(
  ...sets: Array<Iterable<string>>
): Set<string> {
  const result = new Set<string>();
  for (const set of sets) {
    for (const email of set) {
      const normalized = normalizeAnnouncementEmail(email);
      if (normalized) {
        result.add(normalized);
      }
    }
  }
  return result;
}

export function filterRecipientsNotRecentlyEmailed<
  T extends { email: string },
>(
  recipients: T[],
  recentlyEmailed: ReadonlySet<string>,
): { recipients: T[]; skippedCount: number } {
  const eligible: T[] = [];
  let skippedCount = 0;

  for (const recipient of recipients) {
    if (recentlyEmailed.has(normalizeAnnouncementEmail(recipient.email))) {
      skippedCount++;
      continue;
    }
    eligible.push(recipient);
  }

  return { recipients: eligible, skippedCount };
}

export function isAnnouncementCampaignActive(
  status: AnnouncementCampaignStatus,
): boolean {
  return status === "pending" || status === "sending";
}

export function parseAnnouncementScheduledAt(
  value: unknown,
): Date | null {
  if (value == null || value === "") {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value !== "string") {
    return null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function isAnnouncementCampaignDue(
  scheduledAt: Date | string | null | undefined,
  now: Date = new Date(),
): boolean {
  const parsed = parseAnnouncementScheduledAt(scheduledAt ?? null);
  if (!parsed) {
    return true;
  }
  return parsed.getTime() <= now.getTime();
}

export function announcementCampaignMsUntilDue(
  scheduledAt: Date | string | null | undefined,
  now: Date = new Date(),
): number {
  const parsed = parseAnnouncementScheduledAt(scheduledAt ?? null);
  if (!parsed) {
    return 0;
  }
  return Math.max(0, parsed.getTime() - now.getTime());
}

export function announcementCampaignTimerDelayMs(
  scheduledAt: Date | string | null | undefined,
  now: Date = new Date(),
): number {
  const untilDue = announcementCampaignMsUntilDue(scheduledAt, now);
  if (untilDue <= 0) {
    return 0;
  }
  return Math.min(untilDue, ANNOUNCEMENT_SCHEDULE_TIMER_MAX_MS);
}

export function isAnnouncementCampaignWaitingToSend(input: {
  status: AnnouncementCampaignStatus;
  scheduledAt?: Date | string | null;
  now?: Date;
}): boolean {
  return (
    input.status === "pending" &&
    !isAnnouncementCampaignDue(input.scheduledAt, input.now)
  );
}

export function shouldProcessAnnouncementCampaign(input: {
  status: AnnouncementCampaignStatus;
  scheduledAt?: Date | string | null;
  now?: Date;
}): boolean {
  if (!isAnnouncementCampaignActive(input.status)) {
    return false;
  }
  return isAnnouncementCampaignDue(input.scheduledAt, input.now);
}

export function canCancelAnnouncementCampaign(input: {
  status: AnnouncementCampaignStatus;
  scheduledAt?: Date | string | null;
  now?: Date;
}): boolean {
  return input.status === "pending";
}

export type ResolveAnnouncementScheduledAtResult =
  | { ok: true; scheduledAt: Date | null }
  | { ok: false; error: "scheduledAtInvalid" | "scheduledAtInPast" };

export function resolveAnnouncementScheduledAt(
  value: unknown,
  now: Date = new Date(),
): ResolveAnnouncementScheduledAtResult {
  if (value == null || value === "") {
    return { ok: true, scheduledAt: null };
  }
  const parsed = parseAnnouncementScheduledAt(value);
  if (!parsed) {
    return { ok: false, error: "scheduledAtInvalid" };
  }
  if (parsed.getTime() <= now.getTime()) {
    return { ok: false, error: "scheduledAtInPast" };
  }
  return { ok: true, scheduledAt: parsed };
}

export type AnnouncementCampaignStatusMessageKey =
  | "statusScheduled"
  | "statusPending"
  | "statusSending"
  | "statusCompleted"
  | "statusFailed"
  | "statusCancelled";

export function announcementCampaignStatusMessageKey(input: {
  status: AnnouncementCampaignStatus;
  scheduledAt?: Date | string | null;
  now?: Date;
}): AnnouncementCampaignStatusMessageKey {
  if (
    input.status === "pending" &&
    !isAnnouncementCampaignDue(input.scheduledAt, input.now)
  ) {
    return "statusScheduled";
  }
  if (input.status === "pending") {
    return "statusPending";
  }
  if (input.status === "sending") {
    return "statusSending";
  }
  if (input.status === "completed") {
    return "statusCompleted";
  }
  if (input.status === "cancelled") {
    return "statusCancelled";
  }
  return "statusFailed";
}

export function summarizeAnnouncementCampaign(input: {
  pendingCount: number;
  sentCount: number;
  failedCount: number;
  skippedCount: number;
}): {
  totalCount: number;
  processedCount: number;
  progressPercent: number;
  isComplete: boolean;
} {
  const totalCount =
    input.pendingCount +
    input.sentCount +
    input.failedCount +
    input.skippedCount;
  const processedCount =
    input.sentCount + input.failedCount + input.skippedCount;

  return {
    totalCount,
    processedCount,
    progressPercent:
      totalCount === 0 ? 0 : Math.round((processedCount / totalCount) * 100),
    isComplete: input.pendingCount === 0 && totalCount > 0,
  };
}

export function announcementCampaignLockExpired(
  processingUntil: Date | string | null | undefined,
  now: Date = new Date(),
): boolean {
  if (!processingUntil) {
    return true;
  }
  const until =
    processingUntil instanceof Date
      ? processingUntil
      : new Date(processingUntil);
  if (Number.isNaN(until.getTime())) {
    return true;
  }
  return until.getTime() <= now.getTime();
}

export interface ResendListedEmail {
  id?: string;
  to?: string | string[] | null;
  from?: string | null;
  createdAt?: string | Date | null;
  subject?: string | null;
}

export function resendEmailCreatedAt(
  value: string | Date | null | undefined,
): Date | null {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function collectResendEmailsInWindow(
  emails: ResendListedEmail[],
  options: { since: Date; fromEmail?: string | null },
): { emails: string[]; reachedOlderThanWindow: boolean } {
  const fromEmail = options.fromEmail
    ? parseEmailFromAddress(options.fromEmail).email.toLowerCase()
    : null;
  const collected = new Set<string>();
  let reachedOlderThanWindow = false;

  for (const email of emails) {
    const createdAt = resendEmailCreatedAt(email.createdAt);
    if (createdAt && createdAt < options.since) {
      reachedOlderThanWindow = true;
      continue;
    }
    if (fromEmail && email.from) {
      const listedFrom = parseEmailFromAddress(email.from).email.toLowerCase();
      if (listedFrom !== fromEmail) {
        continue;
      }
    }
    const tos = Array.isArray(email.to)
      ? email.to
      : email.to
        ? [email.to]
        : [];
    for (const to of tos) {
      collected.add(normalizeAnnouncementEmail(to));
    }
  }

  return { emails: [...collected], reachedOlderThanWindow };
}

export function shouldStopPagingResendEmails(
  emails: ResendListedEmail[],
  since: Date,
): boolean {
  if (emails.length === 0) {
    return true;
  }
  const last = emails[emails.length - 1];
  const createdAt = resendEmailCreatedAt(last?.createdAt);
  return createdAt != null && createdAt < since;
}

export async function collectRecentResendRecipientEmailsFromPages(
  listPage: (after?: string) => Promise<{
    emails: ResendListedEmail[];
    hasMore: boolean;
  }>,
  options: {
    since: Date;
    fromEmail?: string | null;
    maxPages?: number;
  },
): Promise<string[]> {
  const collected = new Set<string>();
  let after: string | undefined;
  const maxPages = options.maxPages ?? MAX_RESEND_LIST_PAGES;

  for (let page = 0; page < maxPages; page++) {
    const result = await listPage(after);
    const mapped = collectResendEmailsInWindow(result.emails, options);
    for (const email of mapped.emails) {
      collected.add(email);
    }
    if (
      !result.hasMore ||
      result.emails.length === 0 ||
      shouldStopPagingResendEmails(result.emails, options.since)
    ) {
      break;
    }
    const lastId = result.emails[result.emails.length - 1]?.id;
    if (!lastId) {
      break;
    }
    after = lastId;
  }

  return [...collected];
}
